import { Box, CircularProgress } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { trTR } from '@mui/material/locale';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import axios from 'axios';
import React, { Suspense, lazy, useEffect, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';

// Bileşenler
import Layout from './components/Layout';

// Servisler
import { checkAuth } from './services/authService';

// Kritik sayfalar - hemen yüklensin
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';

// Lazy loading ile yüklenecek sayfalar
const Dashboard = lazy(() => import('./pages/Dashboard'));
const DashboardTest = lazy(() => import('./pages/DashboardTest'));
const Stores = lazy(() => import('./pages/Stores'));
const StoreDetail = lazy(() => import('./pages/StoreDetail'));
const Questions = lazy(() => import('./pages/Questions'));
const QuestionDetail = lazy(() => import('./pages/QuestionDetail'));
const ImportQuestions = lazy(() => import('./pages/ImportQuestions'));
const Customers = lazy(() => import('./pages/Customers'));
const CustomerDetail = lazy(() => import('./pages/CustomerDetail'));
const WhatsAppStores = lazy(() => import('./pages/WhatsAppStores'));
const WhatsAppConversations = lazy(() => import('./pages/WhatsAppConversations'));

// Diğer lazy loading sayfalar
const Products = lazy(() => import('./pages/Products'));
const Settings = lazy(() => import('./pages/Settings'));
const StoreEdit = lazy(() => import('./pages/StoreEdit'));
const StoreNew = lazy(() => import('./pages/StoreNew'));

// WhatsApp sayfaları
const WhatsAppStoreNew = lazy(() => import('./pages/WhatsAppStoreNew'));
const WhatsAppStoreDetail = lazy(() => import('./pages/WhatsAppStoreDetail'));
const WhatsAppStoreEdit = lazy(() => import('./pages/WhatsAppStoreEdit'));

// Ürün Sayfaları - Lazy loading
const FAQ = lazy(() => import('./pages/product/FAQ'));
const Features = lazy(() => import('./pages/product/Features'));
const Integrations = lazy(() => import('./pages/product/Integrations'));
const Pricing = lazy(() => import('./pages/product/Pricing'));

// Şirket Sayfaları - Lazy loading
const Blog = lazy(() => import('./pages/company/Blog'));
const Careers = lazy(() => import('./pages/company/Careers'));
const Contact = lazy(() => import('./pages/company/Contact'));

// Kaynak Sayfaları - Lazy loading
const Community = lazy(() => import('./pages/resources/Community'));
const Documentation = lazy(() => import('./pages/resources/Documentation'));
const Support = lazy(() => import('./pages/resources/Support'));
const Tutorials = lazy(() => import('./pages/resources/Tutorials'));

// Yasal Sayfalar - Lazy loading
const AboutUs = lazy(() => import('./pages/legal/AboutUs'));
const CookiePolicy = lazy(() => import('./pages/legal/CookiePolicy'));
const KVKK = lazy(() => import('./pages/legal/KVKK'));
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy'));
const TermsOfUse = lazy(() => import('./pages/legal/TermsOfUse'));

// SEO Sayfaları - Lazy loading
const PazaryeriOtomatikSoruCevaplama = lazy(() => import('./pages/seo/PazaryeriOtomatikSoruCevaplama'));


// Loading bileşeni
const PageLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
    <CircularProgress />
  </Box>
);

// Tema - Helpigo (helpigo.app) mavi vurgu + nötr tipografi
const theme = createTheme({
  palette: {
    primary: {
      main: '#000000',
      dark: '#000000',
      light: '#1a1a1a',
    },
    secondary: {
      main: '#1a1a1a',
      dark: '#000000',
      light: '#6b7280',
    },
    accent: {
      main: '#2563eb',
      dark: '#1d4ed8',
      light: '#3b82f6',
    },
    background: {
      default: '#FFFFFF',
      paper: '#FFFFFF',
      light: '#f7f8fa', // Çok açık gri - Section backgrounds
    },
    text: {
      primary: '#000000',
      secondary: '#6b7280', // Orta gri - Secondary text
    },
    grey: {
      50: '#f7f8fa',
      100: '#e3e8ee', // Açık gri - Borders
      200: '#e3e8ee',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280',
      600: '#4b5563',
      700: '#374151',
      800: '#1a1a1a',
      900: '#000000',
    },
  },
  typography: {
    fontFamily: '"Rubik", "Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 800,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 700,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    body1: {
      fontWeight: 400,
    },
    body2: {
      fontWeight: 400,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0 1px 3px rgba(0, 0, 0, 0.05)',
    '0 4px 6px rgba(0, 0, 0, 0.05)',
    '0 10px 15px rgba(0, 0, 0, 0.05)',
    '0 20px 25px rgba(0, 0, 0, 0.05)',
    '0 25px 50px rgba(0, 0, 0, 0.1)',
    '0 25px 50px rgba(0, 0, 0, 0.1)',
    '0 25px 50px rgba(0, 0, 0, 0.1)',
    '0 25px 50px rgba(0, 0, 0, 0.1)',
    '0 25px 50px rgba(0, 0, 0, 0.1)',
    '0 25px 50px rgba(0, 0, 0, 0.1)',
    '0 25px 50px rgba(0, 0, 0, 0.1)',
    '0 25px 50px rgba(0, 0, 0, 0.1)',
    '0 25px 50px rgba(0, 0, 0, 0.1)',
    '0 25px 50px rgba(0, 0, 0, 0.1)',
    '0 25px 50px rgba(0, 0, 0, 0.1)',
    '0 25px 50px rgba(0, 0, 0, 0.1)',
    '0 25px 50px rgba(0, 0, 0, 0.1)',
    '0 25px 50px rgba(0, 0, 0, 0.1)',
    '0 25px 50px rgba(0, 0, 0, 0.1)',
    '0 25px 50px rgba(0, 0, 0, 0.1)',
    '0 25px 50px rgba(0, 0, 0, 0.1)',
    '0 25px 50px rgba(0, 0, 0, 0.1)',
    '0 25px 50px rgba(0, 0, 0, 0.1)',
    '0 25px 50px rgba(0, 0, 0, 0.1)',
  ],
}, trTR);

// Hata yakalama bileşeni
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    console.error("Hata yakalandı:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 20, textAlign: 'center' }}>
          <h2>Bir şeyler yanlış gitti.</h2>
          <details style={{ whiteSpace: 'pre-wrap', textAlign: 'left' }}>
            <summary>Hata detayları</summary>
            {this.state.error && this.state.error.toString()}
            <br />
            {this.state.errorInfo && this.state.errorInfo.componentStack}
          </details>
        </div>
      );
    }

    return this.props.children;
  }
}

// Yönlendirme bileşenleri
const StoreRedirect = () => {
  const { storeId } = useParams();
  return <Navigate to={`/dashboard/stores/${storeId}`} replace />;
};

const StoreEditRedirect = () => {
  const { storeId } = useParams();
  return <Navigate to={`/dashboard/stores/${storeId}/edit`} replace />;
};

const QuestionRedirect = () => {
  const { questionId } = useParams();
  return <Navigate to={`/dashboard/questions/${questionId}`} replace />;
};

const TestRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/test/${id}`} replace />;
};


function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Kullanıcı oturumunu kontrol et
    const checkAuthentication = async () => {
      try {
        console.log('Kimlik doğrulama kontrolü başlatılıyor...');
        const isLoggedIn = await checkAuth();
        console.log('Kimlik doğrulama sonucu:', isLoggedIn);
        setIsAuthenticated(isLoggedIn);
      } catch (error) {
        console.error('Authentication check failed:', error);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthentication();
  }, []);

  // Render'ın uygulamayı uyku moduna almasını önlemek için keep-alive mekanizması
  useEffect(() => {
    // Health check endpoint'ine 10 dakikada bir istek gönder
    const keepAliveInterval = setInterval(() => {
      axios.get('https://hisonetv2-backend.onrender.com/health')
        .then(response => console.log('Keep-alive ping successful:', response.data))
        .catch(error => console.error('Keep-alive ping failed:', error));
    }, 10 * 60 * 1000); // 10 dakika

    return () => clearInterval(keepAliveInterval);
  }, []);

  // Korumalı route bileşeni
  const ProtectedRoute = ({ children }) => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!isAuthenticated) {
      console.log('Kullanıcı kimliği doğrulanmadı, login sayfasına yönlendiriliyor...');
      return <Navigate to="/login" />;
    }

    console.log('Kullanıcı kimliği doğrulandı, korumalı içerik gösteriliyor...');
    return children;
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  console.log('App render ediliyor, kimlik durumu:', isAuthenticated);

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Açık sayfalar - Herkes erişebilir */}
              <Route path="/" element={<Home />} />
              <Route path="/login" element={!isAuthenticated ? <Login setIsAuthenticated={setIsAuthenticated} /> : <Navigate to="/dashboard" />} />
              <Route path="/register" element={!isAuthenticated ? <Register setIsAuthenticated={setIsAuthenticated} /> : <Navigate to="/dashboard" />} />

              {/* Yasal Sayfalar */}
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfUse />} />
              <Route path="/kvkk" element={<KVKK />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />

              {/* Ürün Sayfaları */}
              <Route path="/features" element={<Features />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/integrations" element={<Integrations />} />
              <Route path="/faq" element={<FAQ />} />

              {/* Şirket Sayfaları */}
              <Route path="/blog" element={<Blog />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/contact" element={<Contact />} />

              {/* Kaynak Sayfaları */}
              <Route path="/docs" element={<Documentation />} />
              <Route path="/support" element={<Support />} />
              <Route path="/community" element={<Community />} />
              <Route path="/tutorials" element={<Tutorials />} />

              {/* SEO Sayfaları */}
              <Route path="/pazaryeri-otomatik-soru-cevaplama" element={<PazaryeriOtomatikSoruCevaplama />} />

              {/* Eski URL'lerden yönlendirme */}
              <Route path="/#/privacy-policy" element={<Navigate to="/privacy-policy" replace />} />
              <Route path="/#/terms" element={<Navigate to="/terms" replace />} />
              <Route path="/#/kvkk" element={<Navigate to="/kvkk" replace />} />
              <Route path="/#/cookie-policy" element={<Navigate to="/cookie-policy" replace />} />
              <Route path="/#/about-us" element={<Navigate to="/about-us" replace />} />

              {/* Alternatif rotalar - Doğrudan ilgili dashboard sayfalarına yönlendir */}
              <Route path="/stores" element={<Navigate to="/dashboard/stores" replace />} />
              <Route path="/stores/:storeId" element={<StoreRedirect />} />
              <Route path="/stores/:storeId/edit" element={<StoreEditRedirect />} />
              <Route path="/questions" element={<Navigate to="/dashboard/questions" replace />} />
              <Route path="/questions/:questionId" element={<QuestionRedirect />} />
              <Route path="/products" element={<Navigate to="/dashboard/products" replace />} />
              <Route path="/settings" element={<Navigate to="/dashboard/settings" replace />} />
              <Route path="/import-questions" element={<Navigate to="/dashboard/import-questions" replace />} />
              <Route path="/dashboard/test/:id" element={<TestRedirect />} />

              {/* Test sayfası için ayrı bir route */}
              <Route path="/test/:id" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoader />}>
                    <DashboardTest />
                  </Suspense>
                </ProtectedRoute>
              } />

              {/* Korumalı sayfalar - Sadece giriş yapmış kullanıcılar erişebilir */}
              <Route path="/dashboard/*" element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route index element={
                  <Suspense fallback={<PageLoader />}>
                    <Dashboard />
                  </Suspense>
                } />
                <Route path="stores" element={
                  <Suspense fallback={<PageLoader />}>
                    <Stores />
                  </Suspense>
                } />
                <Route path="stores/new" element={
                  <Suspense fallback={<PageLoader />}>
                    <StoreNew />
                  </Suspense>
                } />
                <Route path="stores/:storeId" element={
                  <Suspense fallback={<PageLoader />}>
                    <StoreDetail />
                  </Suspense>
                } />
                <Route path="stores/:storeId/edit" element={
                  <Suspense fallback={<PageLoader />}>
                    <StoreEdit />
                  </Suspense>
                } />
                <Route path="questions" element={
                  <Suspense fallback={<PageLoader />}>
                    <Questions />
                  </Suspense>
                } />
                <Route path="questions/:questionId" element={
                  <Suspense fallback={<PageLoader />}>
                    <QuestionDetail />
                  </Suspense>
                } />
                <Route path="customers" element={
                  <Suspense fallback={<PageLoader />}>
                    <Customers />
                  </Suspense>
                } />
                <Route path="customers/:customerId" element={
                  <Suspense fallback={<PageLoader />}>
                    <CustomerDetail />
                  </Suspense>
                } />
                <Route path="products" element={
                  <Suspense fallback={<PageLoader />}>
                    <Products />
                  </Suspense>
                } />
                <Route path="settings" element={
                  <Suspense fallback={<PageLoader />}>
                    <Settings />
                  </Suspense>
                } />
                <Route path="import-questions" element={
                  <Suspense fallback={<PageLoader />}>
                    <ImportQuestions />
                  </Suspense>
                } />
                {/* WhatsApp Routes */}
                <Route path="whatsapp/stores" element={
                  <Suspense fallback={<PageLoader />}>
                    <WhatsAppStores />
                  </Suspense>
                } />
                <Route path="whatsapp/stores/new" element={
                  <Suspense fallback={<PageLoader />}>
                    <WhatsAppStoreNew />
                  </Suspense>
                } />
                <Route path="whatsapp/stores/:storeId" element={
                  <Suspense fallback={<PageLoader />}>
                    <WhatsAppStoreDetail />
                  </Suspense>
                } />
                <Route path="whatsapp/stores/:storeId/edit" element={
                  <Suspense fallback={<PageLoader />}>
                    <WhatsAppStoreEdit />
                  </Suspense>
                } />
                <Route path="whatsapp/conversations" element={
                  <Suspense fallback={<PageLoader />}>
                    <WhatsAppConversations />
                  </Suspense>
                } />
              </Route>

              {/* Bilinmeyen rotaları ana sayfaya yönlendir */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </ThemeProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App; 